import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import Avatar from '@/components/Avatar';
import FormattedText from '@/components/FormattedText';
import VerifiedBadgeIcon from '@/components/icons/VerifiedBadgeIcon';
import { Skeleton, SkeletonCircle } from '@/components/Skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { communityService, CommunityPost } from '@/services/communityService';
import { apiService } from '@/services/apiService';
import ActionBottomSheet from '@/components/ActionBottomSheet';
import Toast from '@/components/Toast';
import CommunityMeetingsSection from '@/components/CommunityMeetingsSection';

interface ProfileUser {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  is_admin?: boolean;
  username?: string | null;
}

export default function CommunityProfileScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { user, userProfile } = useAuth();
  const params = useLocalSearchParams();
  
  const [targetUserId, setTargetUserId] = useState(params.userId as string || '');
  const isVerifiedParam = params.verified === 'true';
  
  const [profileUser, setProfileUser] = useState<ProfileUser | null>(null);
  const [userPosts, setUserPosts] = useState<CommunityPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [profileStats, setProfileStats] = useState({ following: 0, friends: 0, posts: 0 });
  
  const isSelfProfile = user?.id === targetUserId;
  
  const isVerified = isVerifiedParam || profileUser?.is_admin === true;

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const handlePostMenuPress = (post: CommunityPost) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setSelectedPost(post);
    setActionMenuVisible(true);
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    
    try {
      const success = await communityService.deletePost(selectedPost.id);
      if (success) {
        setUserPosts(userPosts.filter(p => p.id !== selectedPost.id));
        showToast(t('communityProfile.postRemovedSuccess'), 'success');
      } else {
        showToast(t('communityProfile.postRemoveError'), 'error');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      showToast(t('communityProfile.postRemoveError'), 'error');
    }
    
    setActionMenuVisible(false);
    setSelectedPost(null);
  };

  const handleEditPost = () => {
    if (!selectedPost) return;
    
    setActionMenuVisible(false);
    
    router.push({
      pathname: '/community-new-post',
      params: {
        editMode: 'true',
        postId: selectedPost.id,
        existingContent: selectedPost.content,
        existingTopics: JSON.stringify(selectedPost.topics || []),
      },
    });
    
    setSelectedPost(null);
  };

  useEffect(() => {
    const usernameParam = params.username as string;
    if (!targetUserId && usernameParam) {
      resolveUsernameToId(usernameParam);
    } else {
      loadProfileData();
    }
  }, [targetUserId]);

  const resolveUsernameToId = async (username: string) => {
    // Self-mention shortcut: if the username matches the logged-in user, use their id directly
    if (
      user?.id &&
      userProfile?.username &&
      userProfile.username.toLowerCase() === username.toLowerCase()
    ) {
      setTargetUserId(user.id);
      return;
    }

    try {
      const response = await apiService.get<{ success: boolean; user: ProfileUser | null }>(
        `/api/community/users/resolve?username=${encodeURIComponent(username)}`
      );
      if (response.success && response.data?.user) {
        const resolved = response.data.user;
        // Pre-populate profile header immediately so the screen never shows anonymous
        setProfileUser(resolved);
        setTargetUserId(resolved.id);
        return;
      }
    } catch (error) {
      console.error('Error resolving username:', error);
    }
    setIsLoading(false);
  };

  const loadProfileData = async () => {
    if (!targetUserId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch posts, follow status, and stats in parallel
      const [postsResult, followStatus, stats] = await Promise.all([
        communityService.getUserPosts(targetUserId),
        communityService.checkFollowingStatus(targetUserId),
        communityService.getUserStats(targetUserId),
      ]);
      
      setIsFollowing(followStatus.isFollowing);
      setProfileStats(stats);
      
      if (postsResult.posts && postsResult.posts.length > 0) {
        setUserPosts(postsResult.posts);
        
        const firstPost = postsResult.posts[0];
        if (firstPost && firstPost.author) {
          setProfileUser({
            id: firstPost.author.id,
            full_name: firstPost.author.full_name,
            avatar_url: firstPost.author.avatar_url,
            created_at: firstPost.author.created_at || firstPost.created_at,
            is_admin: firstPost.author.is_admin,
            username: firstPost.author.username,
          });
        }
      } else if (isSelfProfile && userProfile) {
        setProfileUser({
          id: user?.id || '',
          full_name: userProfile.full_name,
          avatar_url: userProfile.avatar_url,
          created_at: userProfile.created_at || new Date().toISOString(),
          is_admin: userProfile.is_admin,
          username: userProfile.username,
        });
      } else {
        // No posts found — fetch basic profile info directly so the header is never empty
        try {
          const profileResponse = await apiService.get<{ success: boolean; user: ProfileUser }>(
            `/api/community/users/${targetUserId}/profile`
          );
          if (profileResponse.success && profileResponse.data?.user) {
            setProfileUser(profileResponse.data.user);
          }
        } catch (profileError) {
          console.error('Error fetching profile user info:', profileError);
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      if (isSelfProfile && userProfile) {
        setProfileUser({
          id: user?.id || '',
          full_name: userProfile.full_name,
          avatar_url: userProfile.avatar_url,
          created_at: userProfile.created_at || new Date().toISOString(),
          is_admin: userProfile.is_admin,
          username: userProfile.username,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    router.back();
  };

  const handleFollow = async () => {
    if (isFollowLoading) return;
    
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setIsFollowLoading(true);
    
    try {
      const wasFollowing = isFollowing;
      setIsFollowing(!wasFollowing); // Optimistic update
      
      let success: boolean;
      if (wasFollowing) {
        success = await communityService.unfollowUser(targetUserId);
      } else {
        success = await communityService.followUser(targetUserId);
      }
      
      if (!success) {
        setIsFollowing(wasFollowing); // Revert on failure
        showToast(t('community.followError') || 'Failed to update follow status', 'error');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      showToast(t('community.followError') || 'Failed to update follow status', 'error');
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleEditProfile = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    router.push('/perfil');
  };

  const formatMemberSince = (dateString: string): string => {
    const date = new Date(dateString);
    const localeMap: { [key: string]: string } = {
      pt: 'pt-BR',
      en: 'en-US',
      es: 'es-ES',
    };
    const locale = localeMap[language || 'pt'] || 'pt-BR';
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('communityProfile.now');
    if (diffMins < 60) return t('communityProfile.minutesAgo', { count: diffMins.toString() });
    if (diffHours < 24) return t('communityProfile.hoursAgo', { count: diffHours.toString() });
    if (diffDays === 1) return t('communityProfile.yesterday');
    if (diffDays < 7) return t('communityProfile.daysAgo', { count: diffDays.toString() });

    const localeMap: { [key: string]: string } = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };
    const locale = localeMap[language || 'pt'] || 'pt-BR';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  const displayName = profileUser?.full_name || params.name as string || t('communityProfile.anonymous');
  const displayHandle = profileUser?.username ? `@${profileUser.username}` : null;
  const displayAvatar = profileUser?.avatar_url || null;
  const memberSinceDate = profileUser?.created_at 
    ? formatMemberSince(profileUser.created_at)
    : formatMemberSince(new Date().toISOString());

  const PostCard = useCallback(({ post, isLast }: { post: CommunityPost; isLast: boolean }) => {
    const [hasSupported, setHasSupported] = useState(post.user_has_supported || false);

    const handleSupport = (e: any) => {
      e.stopPropagation();
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      setHasSupported(!hasSupported);
    };

    const handleOpenPost = () => {
      router.push({
        pathname: '/community-post',
        params: {
          postId: post.id,
          postUserId: post.user_id,
          author: post.author?.full_name || displayName,
          authorId: post.author?.id || targetUserId,
          avatarUrl: post.author?.avatar_url || displayAvatar || '',
          content: post.content,
          time: formatTimestamp(post.created_at),
          supportCount: post.support_count.toString(),
          encourageCount: post.encourage_count.toString(),
          topic: post.topics?.[0] || '',
          verified: isVerified ? 'true' : 'false',
        },
      });
    };

    const handleEncouragePress = (e: any) => {
      e.stopPropagation();
      handleOpenPost();
    };
    
    return (
      <TouchableOpacity 
        style={[styles.postCard, !isLast && styles.postCardWithDivider]} 
        activeOpacity={0.7}
        onPress={handleOpenPost}
      >
        <View style={styles.avatarColumn}>
          <Avatar
            uri={displayAvatar}
            size={32}
            loading={false}
            showInitials={true}
            initials={displayName}
          />
        </View>
        <View style={styles.postContent}>
          <View style={styles.userHeader}>
            <View style={styles.nameWithBadge}>
              <Text style={styles.authorName}>{displayName}</Text>
              {isVerified && (
                <VerifiedBadgeIcon size={16} />
              )}
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.timestamp}>{formatTimestamp(post.created_at)}</Text>
              {isSelfProfile && (
                <TouchableOpacity 
                  style={styles.postMenuButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    handlePostMenuPress(post);
                  }}
                >
                  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <Path d="M10 5.5C10.8284 5.5 11.5 4.82843 11.5 4C11.5 3.17157 10.8284 2.5 10 2.5C9.17157 2.5 8.5 3.17157 8.5 4C8.5 4.82843 9.17157 5.5 10 5.5Z" fill="#757575"/>
                    <Path d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z" fill="#757575"/>
                    <Path d="M10 17.5C10.8284 17.5 11.5 16.8284 11.5 16C11.5 15.1716 10.8284 14.5 10 14.5C9.17157 14.5 8.5 15.1716 8.5 16C8.5 16.8284 9.17157 17.5 10 17.5Z" fill="#757575"/>
                  </Svg>
                </TouchableOpacity>
              )}
            </View>
          </View>
          <FormattedText 
            content={post.content}
            style={styles.noteContent}
          />
          <View style={styles.engagementSection}>
            <TouchableOpacity style={styles.engagementItem} onPress={handleSupport}>
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path 
                  d="M12.0017 5.92507C11.3979 5.24903 10.6225 4.72728 9.76648 4.42682C6.4652 3.25658 3.38047 5.59684 3.03157 8.87727C2.5431 13.8208 7.83511 16.8611 11.1366 19.572C11.5082 19.8819 12.0512 19.967 12.4989 19.7815C12.7138 19.7 12.8866 19.5452 13.0616 19.4014C16.3914 16.6725 21.6522 13.5358 20.9334 8.55669C20.401 5.07868 16.7703 3.12597 13.6389 4.67763C13.0203 4.98409 12.4606 5.40932 12.0017 5.92507Z" 
                  fill={hasSupported ? "#DE5609" : "none"}
                  stroke={hasSupported ? "#DE5609" : "#757575"}
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
              <Text style={styles.engagementCount}>{post.support_count + (hasSupported !== (post.user_has_supported || false) ? (hasSupported ? 1 : -1) : 0)}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.engagementItem} 
              onPress={handleEncouragePress}
            >
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path 
                  fillRule="evenodd" 
                  clipRule="evenodd" 
                  d="M9.44015 18.0968L10.8048 19.4662C11.3597 20.0229 12.2573 20.0229 12.8112 19.4662L14.1767 18.0968C14.5653 17.707 15.0921 17.4886 15.6414 17.4886H16.6669C18.7555 17.4886 20.448 15.7895 20.448 13.6938V7.03212C20.448 4.93651 18.7555 3.23828 16.6669 3.23828H6.94996C4.86141 3.23828 3.16797 4.93651 3.16797 7.03212V13.6938C3.16797 15.7895 4.86141 17.4886 6.94996 17.4886H7.97554C8.52477 17.4886 9.05158 17.707 9.44015 18.0968Z" 
                  stroke="#757575" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
              <Text style={styles.engagementCount}>{post.encourage_count}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [displayName, displayAvatar, isVerified, router, targetUserId]);

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path 
                d="M15 18L9 12L15 6" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('communityProfile.loading')}</Text>
          <View style={styles.headerSpacer} />
        </View>
        <View style={styles.skeletonContainer}>
          <View style={styles.skeletonProfileSection}>
            <SkeletonCircle size={88} />
            <Skeleton width={150} height={20} style={{ marginTop: 16 }} />
            <Skeleton width={100} height={14} style={{ marginTop: 8 }} />
            <Skeleton width={180} height={40} borderRadius={20} style={{ marginTop: 16 }} />
          </View>
          <View style={styles.skeletonPostsSection}>
            <View style={styles.skeletonPost}>
              <SkeletonCircle size={32} />
              <View style={styles.skeletonPostContent}>
                <Skeleton width={100} height={12} />
                <Skeleton width="90%" height={14} style={{ marginTop: 8 }} />
                <Skeleton width="70%" height={14} style={{ marginTop: 4 }} />
              </View>
            </View>
            <View style={[styles.skeletonPost, { marginTop: 16 }]}>
              <SkeletonCircle size={32} />
              <View style={styles.skeletonPostContent}>
                <Skeleton width={80} height={12} />
                <Skeleton width="80%" height={14} style={{ marginTop: 8 }} />
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path 
              d="M15 18L9 12L15 6" 
              stroke="#151515" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </Svg>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{displayHandle || displayName}</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.profileSection}>
          <Avatar 
            uri={displayAvatar} 
            size={88} 
            loading={false}
            showInitials={true}
            initials={displayName}
          />
          
          <View style={styles.nameSection}>
            <View style={styles.nameWithBadgeRow}>
              <Text style={styles.profileName}>{displayName}</Text>
              {isVerified && (
                <VerifiedBadgeIcon size={20} />
              )}
            </View>
            <Text style={styles.memberSince}>{t('communityProfile.memberSince', { date: memberSinceDate })}</Text>
          </View>

          <View style={styles.statsRow}>
            <Text style={styles.statsText}>
              <Text style={styles.statsNumber}>{profileStats.following}</Text> {t('communityProfile.statsFollowing')}
            </Text>
            <Text style={styles.statsText}>
              <Text style={styles.statsNumber}>{profileStats.friends}</Text> {t('communityProfile.statsFriends')}
            </Text>
            <Text style={styles.statsText}>
              <Text style={styles.statsNumber}>{profileStats.posts}</Text> {t('communityProfile.statsPosts')}
            </Text>
          </View>

          <View style={styles.actionButtons}>
            {isSelfProfile ? (
              <TouchableOpacity 
                style={styles.editProfileButton}
                onPress={handleEditProfile}
              >
                <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
                <Text style={styles.editProfileButtonText}>{t('communityProfile.editProfile')}</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity 
                style={[
                  styles.followButton, 
                  isFollowing && styles.followingButton,
                  isFollowLoading && { opacity: 0.6 }
                ]}
                onPress={handleFollow}
                disabled={isFollowLoading}
              >
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path 
                    d="M5.5 12.5L10.167 17L18.5 7" 
                    stroke={isFollowing ? "#151515" : "#FFFFFF"} 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </Svg>
                <Text style={[styles.followButtonText, isFollowing && styles.followingButtonText]}>
                  {isFollowing ? t('communityProfile.following') : t('communityProfile.follow')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        <View style={styles.divider} />

        <CommunityMeetingsSection userId={targetUserId} />

        <View style={styles.postsSection}>
          {userPosts.length === 0 ? (
            <View style={styles.emptyPosts}>
              <Text style={styles.emptyPostsText}>
                {isSelfProfile 
                  ? t('communityProfile.noPostsYetSelf') 
                  : t('communityProfile.noPostsYet')}
              </Text>
            </View>
          ) : (
            userPosts.map((post, index) => (
              <PostCard 
                key={post.id} 
                post={post} 
                isLast={index === userPosts.length - 1}
              />
            ))
          )}
        </View>
      </ScrollView>

      {/* Action Menu Bottom Sheet */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: actionMenuVisible ? 'auto' : 'none' }}>
        <ActionBottomSheet
          isVisible={actionMenuVisible}
          onClose={() => setActionMenuVisible(false)}
          title={t('communityProfile.options')}
        >
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
              marginBottom: 8,
            }} 
            onPress={handleEditPost}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('communityProfile.editPost')}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
            }} 
            onPress={handleDeletePost}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('communityProfile.deletePost')}</Text>
          </TouchableOpacity>
        </ActionBottomSheet>
      </View>

      {/* Toast */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
  },
  backButton: {
    width: 24,
    height: 24,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  headerSpacer: {
    width: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  skeletonContainer: {
    flex: 1,
    padding: 24,
  },
  skeletonProfileSection: {
    alignItems: 'center',
    paddingBottom: 24,
  },
  skeletonPostsSection: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
  skeletonPost: {
    flexDirection: 'row',
    gap: 12,
  },
  skeletonPostContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  profileSection: {
    padding: 24,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#FFFFFF',
  },
  nameSection: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  nameWithBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  profileName: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
  },
  memberSince: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  profileHandle: {
    fontSize: 13,
    fontFamily: 'Inter_400Regular',
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 4,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
  },
  statsText: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  statsNumber: {
    fontFamily: 'Inter_700Bold',
    fontWeight: '700',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  followButton: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    backgroundColor: '#151515',
    height: 40,
    minHeight: 40,
  },
  followingButton: {
    backgroundColor: '#F0EEE4',
  },
  followButtonText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  followingButtonText: {
    color: '#151515',
  },
  editProfileButton: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    height: 40,
    minHeight: 40,
  },
  editProfileButtonText: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  messageButton: {
    flexDirection: 'row',
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    height: 40,
    minHeight: 40,
  },
  messageButtonText: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  divider: {
    height: 1,
    backgroundColor: '#F9F8F3',
  },
  postsSection: {
    paddingTop: 0,
    paddingBottom: 24,
  },
  emptyPosts: {
    padding: 40,
    alignItems: 'center',
  },
  emptyPostsText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757575',
    textAlign: 'center',
  },
  postCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
  },
  postCardWithDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  avatarColumn: {
    alignItems: 'center',
  },
  postContent: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 8,
  },
  userHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  nameWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  postMenuButton: {
    padding: 4,
  },
  actionCardText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  authorName: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  timestamp: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
  noteContent: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 19.6,
  },
  engagementSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  engagementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  engagementCount: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#757575',
    lineHeight: 19.6,
  },
});
